// FamilySelector - Shows a multi-select family picker when user belongs to multiple families
// If user only belongs to one family, this component renders nothing (data goes to that family automatically)
import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Checkbox,
  ListItemText,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { useFamily, type FamilyInfo } from '../../context';

interface FamilySelectorProps {
  // Currently selected family IDs (controlled component)
  value: string[];
  // Callback when family selection changes
  onChange: (familyIds: string[]) => void;
  // Optional label (default: "Families")
  label?: string;
  // If true, always show the selector even for single family (useful for debugging)
  alwaysShow?: boolean;
  // Optional: make full width
  fullWidth?: boolean;
  // Optional: size variant
  size?: 'small' | 'medium';
}

/**
 * FamilySelector component (Multi-select)
 *
 * Shows a multi-select family picker only when the user belongs to multiple families.
 * If the user belongs to only one family, this component renders nothing
 * and the form should use that family automatically.
 *
 * Usage:
 * ```tsx
 * const { selectedFamilyIds, setSelectedFamilyIds, showSelector } = useFamilySelection();
 *
 * return (
 *   showSelector && (
 *     <FamilySelector
 *       value={selectedFamilyIds}
 *       onChange={setSelectedFamilyIds}
 *     />
 *   )
 * );
 * ```
 */
export const FamilySelector: React.FC<FamilySelectorProps> = ({
  value,
  onChange,
  label = 'Families',
  alwaysShow = false,
  fullWidth = true,
  size = 'medium',
}) => {
  const { allFamilies } = useFamily();

  // Don't render if user only belongs to one family (unless alwaysShow is true)
  if (!alwaysShow && allFamilies.length <= 1) {
    return null;
  }

  // If no families at all, don't render
  if (allFamilies.length === 0) {
    return null;
  }

  const handleChange = (event: any) => {
    const selectedIds = event.target.value as string[];
    // Ensure at least one family is selected
    if (selectedIds.length > 0) {
      onChange(selectedIds);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} size={size as 'small' | 'medium'}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selected as string[]).map((id) => {
              const family = allFamilies.find((f: FamilyInfo) => f.id === id);
              return family ? (
                <Chip key={id} label={family.name} size="small" />
              ) : null;
            })}
          </Box>
        )}
      >
        {allFamilies.map((family: FamilyInfo) => (
          <MenuItem key={family.id} value={family.id}>
            <Checkbox checked={value.includes(family.id)} />
            <ListItemText primary={family.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Hook to manage multi-family selection for forms
 *
 * Returns an object with:
 * - selectedFamilyIds: array of currently selected family IDs
 * - setSelectedFamilyIds: setter for the selection
 * - showSelector: whether to show the family selector UI (true if multiple families)
 * - allFamilies: all families the user belongs to
 *
 * When user belongs to only one family, that family is automatically selected.
 *
 * Usage:
 * ```tsx
 * const { selectedFamilyIds, setSelectedFamilyIds, showSelector } = useFamilySelection();
 *
 * // In form submission - insert data into each selected family:
 * for (const familyId of selectedFamilyIds) {
 *   await insert(data, familyId);
 * }
 * ```
 */
export function useFamilySelection(initialFamilyIds?: string[]) {
  const { allFamilies, currentFamilyId } = useFamily();

  // Default to all families selected, or use initial value
  const getDefaultSelection = React.useCallback(() => {
    if (initialFamilyIds && initialFamilyIds.length > 0) {
      return initialFamilyIds;
    }
    // Default to current family or first family
    if (currentFamilyId) {
      return [currentFamilyId];
    }
    if (allFamilies.length > 0) {
      return [allFamilies[0].id];
    }
    return [];
  }, [initialFamilyIds, currentFamilyId, allFamilies]);

  const [selectedFamilyIds, setSelectedFamilyIds] = React.useState<string[]>(getDefaultSelection);

  // Update selection if families change and nothing is selected
  React.useEffect(() => {
    if (selectedFamilyIds.length === 0 && allFamilies.length > 0) {
      setSelectedFamilyIds([currentFamilyId || allFamilies[0].id]);
    }
  }, [allFamilies, currentFamilyId, selectedFamilyIds.length]);

  // Determine if we should show the selector (only when multiple families)
  const showSelector = allFamilies.length > 1;

  // When only one family, ensure it's always selected
  const effectiveFamilyIds = React.useMemo(() => {
    if (allFamilies.length === 1) {
      return [allFamilies[0].id];
    }
    return selectedFamilyIds.length > 0 ? selectedFamilyIds : [allFamilies[0]?.id].filter(Boolean);
  }, [allFamilies, selectedFamilyIds]);

  return {
    selectedFamilyIds: effectiveFamilyIds,
    setSelectedFamilyIds,
    showSelector,
    allFamilies,
  };
}

export default FamilySelector;
